import { Box, Button, FormControl, Grid, InputLabel, Link, ListSubheader, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, gridClasses, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import {
    fetchClaimsData,
    setClaimNumberSearchValue,
    setClaimManagementSelectedStatus,
    setDosStart,
    setDosEnd,
    setEncounterIdList,
    getStatuses,
    statusValues,
    exportClaimsData,
} from '../../../store/claimsManagement.slice';
import Loading from '../../Shared/Loading';
import { parentUrl } from '../../../utils/constants';
import MessageDisplay from '../../Shared/MessageDisplay';
import { setEncounterChange } from '../../../store/procedureSignOffEncounter.slice';
import { getExportModels } from '../../../utils/exportUtils';
import { fetchHealthPlans } from '../../../store/memberDetail.slice';
import type { RootState } from '../../../reducers';

const ClaimsReport = () => {
    const { claimsData, claimManagementSelectedStatus, dosStart, dosEnd } = useSelector((state: RootState) => state.claimsManagement);
    const { healthPlans } = useSelector((state: RootState) => state.memberDetail);
    const { encounterChange } = useSelector((state: RootState) => state.procedureSignOffEncounter);
    const dispatch = useAppDispatch();
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [paginationModel, setPaginationModel] = useState({ pageSize: 15, page: 0 });
    const [healthPlanId, setHealthPlanId] = useState('All');

    const diagnosisFormatHandler = (params: GridRenderCellParams<any>) => {
        return params.value ? (
            <Stack>
                <Typography>{params.value.Code} -</Typography>
                <Typography color={'#999'}>{params.value.Name}</Typography>
            </Stack>
        ) : (
            <Typography></Typography>
        );
    };

    const columns: GridColDef[] = [
        {
            field: 'TraceId',
            headerName: 'Claim Number',
            flex: 1.35,
        },
        {
            field: 'EncounterStatusValue',
            headerName: 'Status',
            flex: 0.75,
        },
        {
            field: 'DateOfService',
            headerName: 'DOS',
            flex: 0.75,
        },
        {
            field: 'AHCCCSId',
            headerName: 'AHCCCS ID',
            flex: 0.75,
        },
        {
            field: 'MemberName',
            headerName: 'Member Name',
            minWidth: 200,
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Link
                        target={'_blank'}
                        underline="hover"
                        href={`${parentUrl}provider/members-list/member-chart/${params.row.MemberId}/member-profile`}
                        color={'#337ab7'}
                    >
                        {params.value}
                    </Link>
                );
            },
        },
        {
            field: 'ContractingHealthPlan',
            headerName: 'Contracting Health Plan',
            flex: 1,
        },
        {
            field: 'CreatedBy',
            headerName: 'Creating Provider',
            flex: 1,
        },
        {
            field: 'CPTCode',
            headerName: 'CPT Code',
            flex: 0.75,
        },
        {
            field: 'PlaceOfService',
            headerName: 'Place of Service',
            flex: 0.75,
        },
        {
            field: 'PrimaryDiagnosis',
            headerName: 'Primary DX',
            flex: 1,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'SecondaryDiagnosis',
            headerName: 'Secondary DX',
            flex: 1,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'TertiaryDiagnosis',
            headerName: 'Tertiary DX',
            flex: 1,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'QuaternaryDiagnosis',
            headerName: 'Quaternary DX',
            flex: 0.75,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'BilledAmount',
            headerName: 'Billed Amount',
            flex: 0.75,
        },
    ];

    const searchClaims = () => {
        setLoadingMessage('Loading...');
        dispatch(fetchClaimsData(claimManagementSelectedStatus, dosStart, dosEnd, healthPlanId));
    };

    const exportClaims = () => {
        setLoadingMessage('Loading...');
        const exportModels = getExportModels(columns);
        dispatch(exportClaimsData(claimManagementSelectedStatus, dosStart, dosEnd, exportModels, '', healthPlanId));
    };

    useEffect(() => {
        dispatch(setClaimManagementSelectedStatus('13:Claim'));
        dispatch(fetchHealthPlans());
    }, [dispatch]);

    useEffect(() => {
        if (encounterChange) {
            searchClaims();
            dispatch(setEncounterChange(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, encounterChange]);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MessageDisplay />
            <Box>
                {<Loading message={loadingMessage} />}
                <Grid container justifyContent={'flex-end'} spacing={3} style={{ padding: '16px 16px 16px 16px' }}>
                    <Grid size={2} textAlign={'left'}>
                        <Typography variant="h5">Claims</Typography>
                        <Typography
                            variant="h6"
                            className={
                                statusValues.filter((x) => x.type === 'Claim').find((x) => x.key === claimManagementSelectedStatus)?.value > 2
                                    ? 'colorsuccess'
                                    : 'colorerror'
                            }
                        >
                            {statusValues.filter((x) => x.type === 'Claim').find((x) => x.key === claimManagementSelectedStatus)?.value > 2
                                ? 'Exist in Waystar'
                                : 'Not in Waystar'}
                        </Typography>
                        <br />
                        <Stack direction="column" alignItems={'left'} justifyContent={'flex-end'} spacing={3}>
                            <FormControl>
                                <InputLabel id="demo-simple-select-label">Status</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={claimManagementSelectedStatus}
                                    label="Status"
                                    onChange={(e) => {
                                        dispatch(setClaimNumberSearchValue(''));
                                        dispatch(setClaimManagementSelectedStatus(e.target.value));
                                    }}
                                >
                                    {getStatuses(true).map((statusByType, index) => [
                                        <ListSubheader key={index}>{statusByType.Name}</ListSubheader>,
                                        statusByType.List.map((status) => {
                                            return (
                                                <MenuItem key={status.key} value={status.key} sx={{ paddingLeft: 4 }}>
                                                    {status.label}
                                                </MenuItem>
                                            );
                                        }),
                                    ])}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <DesktopDatePicker
                                    label="DOS Start Date:"
                                    value={dosStart}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => {
                                        dispatch(setClaimNumberSearchValue(''));
                                        dispatch(setDosStart(newValue));
                                    }}
                                    slotProps={{ textField: {} }}
                                />
                            </FormControl>
                            <FormControl>
                                <DesktopDatePicker
                                    label="DOS End Date:"
                                    value={dosEnd}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => {
                                        dispatch(setClaimNumberSearchValue(''));
                                        dispatch(setDosEnd(newValue));
                                    }}
                                    slotProps={{ textField: {} }}
                                />
                            </FormControl>
                            <FormControl>
                                <InputLabel id="health-plan-select-label">Health Plan</InputLabel>
                                <Select
                                    labelId="health-plan-select-label"
                                    id="health-plan-select"
                                    value={healthPlanId}
                                    label="Health Plan"
                                    onChange={(e) => {
                                        dispatch(setClaimNumberSearchValue(''));
                                        setHealthPlanId(e.target.value);
                                    }}
                                >
                                    <MenuItem value="All">All</MenuItem>
                                    {healthPlans?.map((plan) => (
                                        <MenuItem key={plan.Id} value={plan.Id}>
                                            {plan.Name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button onClick={searchClaims} style={{ minWidth: '120px!important' }} variant="contained">
                                Search
                            </Button>
                            <Button
                                disabled={!claimsData || claimsData.length === 0}
                                onClick={exportClaims}
                                style={{ minWidth: '120px!important' }}
                                variant="contained"
                            >
                                Export
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid size={10}>
                        <div style={{ height: '88vh', width: '100%' }}>
                            <DataGrid
                                rows={claimsData}
                                columns={columns}
                                onRowSelectionModelChange={(ids) => dispatch(setEncounterIdList(Array.isArray(ids) ? ids : []))}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                pageSizeOptions={[5]}
                                getRowId={(row) => row.EncounterId}
                                getRowHeight={() => 'auto'}
                                sx={{
                                    [`& .${gridClasses.cell}`]: {
                                        py: 2,
                                    },
                                    '& .MuiDataGrid-columnHeaderTitleContainerContent .MuiCheckbox-root': {
                                        // display: 'none',
                                    },
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
            </Box>
        </LocalizationProvider>
    );
};

export default ClaimsReport;
